import React, { useEffect, useState } from 'react';
import { Package, Calendar, Clock, ShoppingBag } from 'lucide-react';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    let savedOrders = [];
    try {
      savedOrders = JSON.parse(localStorage.getItem('orders') || '[]');
    } catch (e) { }

    const validOrders = savedOrders.filter(order => {
      if (!order || !order.date || !order.items || order.items.length === 0) return false;
      return !isNaN(new Date(order.date).getTime());
    });

    validOrders.sort((a, b) => new Date(b.date) - new Date(a.date));
    setOrders(validOrders);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

        .mo-page {
          font-family: 'Sora', sans-serif;
          max-width: 780px;
          margin: 0 auto;
          padding: 2rem 1.25rem 4rem;
          box-sizing: border-box;
          animation: moSlide 0.45s cubic-bezier(0.22,1,0.36,1) both;
        }

        @keyframes moSlide {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Page heading ── */
        .mo-heading {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.75rem;
          font-weight: 800;
          letter-spacing: -0.4px;
          color: var(--text-main, #f1f5f9);
          margin: 0 0 2rem;
        }

        .mo-heading-icon {
          background: linear-gradient(135deg, #60a5fa, #a78bfa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* ── Empty state ── */
        .mo-empty {
          text-align: center;
          padding: 5rem 2rem;
          color: var(--text-muted, #94a3b8);
        }

        .mo-empty-icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: rgba(255,255,255,0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.25rem;
        }

        .mo-empty p {
          font-size: 1.05rem;
          font-weight: 600;
          margin: 0;
        }

        /* ── Order list ── */
        .mo-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        /* ── Order card ── */
        .mo-card {
          background: var(--glass-card, rgba(255,255,255,0.07));
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
          animation: moSlide 0.4s cubic-bezier(0.22,1,0.36,1) both;
        }

        /* ── Card header ── */
        .mo-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          padding: 1.25rem 1.75rem;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          background: rgba(255,255,255,0.03);
        }

        .mo-meta {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          flex-wrap: wrap;
        }

        .mo-meta-chip {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.82rem;
          font-weight: 500;
          color: var(--text-muted, #94a3b8);
          white-space: nowrap;
        }

        .mo-order-id {
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--text-muted, #94a3b8);
          letter-spacing: 0.4px;
          background: rgba(255,255,255,0.06);
          padding: 0.25rem 0.75rem;
          border-radius: 50px;
          border: 1px solid rgba(255,255,255,0.1);
          white-space: nowrap;
        }

        .mo-total {
          font-size: 1.15rem;
          font-weight: 800;
          background: linear-gradient(135deg, #bc555cff, #e84747ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          white-space: nowrap;
          margin-left: 5px;
        }

        /* ── Items body ── */
        .mo-items {
          padding: 1.25rem 1.75rem;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        /* ── Single item row ── */
        .mo-item {
          display: grid;
          grid-template-columns: 56px 1fr auto;
          align-items: center;
          gap: 1rem;
          padding: 0.875rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .mo-item:last-child { border-bottom: none; }

        .mo-item-img {
          width: 56px;
          height: 56px;
          object-fit: cover;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
        }

        .mo-item-info {
          min-width: 0;
        }

        .mo-item-name {
          font-size: 0.97rem;
          font-weight: 700;
          color: var(--text-main, #f1f5f9);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-bottom: 0.2rem;
        }

        .mo-item-qty {
          font-size: 0.82rem;
          color: var(--text-muted, #94a3b8);
          font-weight: 500;
        }

        .mo-item-price {
          font-size: 0.97rem;
          font-weight: 700;
          color: var(--text-main, #f1f5f9);
          white-space: nowrap;
          text-align: right;
        }

        /* ── Card footer ── */
        .mo-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.75rem;
          background: rgba(255,255,255,0.02);
          border-top: 1px solid rgba(255,255,255,0.06);
        }

        .mo-items-count {
          font-size: 0.82rem;
          color: var(--text-muted, #94a3b8);
          font-weight: 500;
        }

        .mo-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          background: rgba(16,185,129,0.12);
          color: #10b981;
          border: 1px solid rgba(16,185,129,0.22);
          padding: 0.28rem 0.85rem;
          border-radius: 50px;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.3px;
        }

        .mo-status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #10b981;
        }

        /* ── Mobile ── */
        @media (max-width: 540px) {
          .mo-page { padding: 1.5rem 1rem 3rem; }

          .mo-card-header {
            flex-direction: column;
            align-items: flex-start;
            padding: 1.1rem 1.25rem;
          }

          .mo-total { font-size: 1.1rem; }

          .mo-items { padding: 1rem 1.25rem; }

          .mo-card-footer { padding: 0.875rem 1.25rem; }

          .mo-item { grid-template-columns: 48px 1fr auto; gap: 0.75rem; }

          .mo-item-img { width: 48px; height: 48px; border-radius: 10px; }

          .mo-heading { font-size: 1.45rem; }
        }
      `}</style>

      <div className="mo-page">

        <h1 className="mo-heading">
          <Package size={28} className="mo-heading-icon" />
          My Orders
        </h1>

        {orders.length === 0 ? (
          <div className="mo-empty">
            <div className="mo-empty-icon">
              <ShoppingBag size={32} color="#94a3b8" />
            </div>
            <p>No orders yet. Start shopping!</p>
          </div>
        ) : (
          <div className="mo-list">
            {orders.map((order, idx) => {
              const orderDate = new Date(order.date);
              const formattedDate = orderDate.toLocaleDateString();
              const formattedTime = orderDate.toLocaleTimeString(undefined, {
                hour: '2-digit', minute: '2-digit'
              });
              const itemCount = order.items?.reduce((sum, i) => sum + (i.quantity || 1), 0) || 0;

              return (
                <div
                  key={order.orderId}
                  className="mo-card"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >

                  {/* Header */}
                  <div className="mo-card-header">
                    <div className="mo-meta">
                      <span className="mo-meta-chip">
                        <Calendar size={13} /> {formattedDate}
                      </span>
                      <span className="mo-meta-chip">
                        <Clock size={13} /> {formattedTime}
                      </span>
                      {order.orderId && (
                        <span className="mo-order-id">{order.orderId}</span>
                      )}
                    </div>
                    <span className="mo-total">₹{order.totalAmount?.toFixed(2)}</span>
                  </div>

                  {/* Items */}
                  <div className="mo-items">
                    {order.items?.map(item => (
                      <div key={item.id} className="mo-item">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="mo-item-img"
                        />
                        <div className="mo-item-info">
                          <div className="mo-item-name">{item.name}</div>
                          <div className="mo-item-qty">Qty: {item.quantity}</div>
                        </div>
                        <div className="mo-item-price">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="mo-card-footer">
                    <span className="mo-items-count">
                      {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </span>
                    <span className="mo-status-badge">
                      <span className="mo-status-dot" /> Confirmed
                    </span>
                  </div>

                </div>
              );
            })}
          </div>
        )}

      </div>
    </>
  );
};

export default MyOrders;